import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDeleteReportSection } from '@hooks/deleteQueries';
import { useReport } from '@hooks/getQueries';
import { useAddToReport, useUpdateReport, useUpdateReportIndex } from '@hooks/putQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import { DeleteOutlineOutlined, EditOutlined } from '@mui/icons-material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { LoadingButton } from '@mui/lab';
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import htmlToPdfmake from 'html-to-pdfmake';
import { min } from 'lodash';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
// import ImageResize from 'quill-image-resize-module-react';
import QuillResizeImage from 'quill-resize-image';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactQuill, { Quill } from 'react-quill-new';
import { useParams } from 'react-router';
import { SortableItem } from './SortableItem';

Quill.register('modules/imageResize', QuillResizeImage);

// pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.vfs = pdfFonts;

const useStyles = makeStyles(() => ({
  quill: {
    '& .ql-toolbar': {
      background: '#F2F2F2 !important',
      border: '1px solid #E8E8E8',
      borderRadius: '6px 6px 0 0',
    },
    '& .ql-container': {
      border: '1px solid #E8E8E8',
      borderRadius: '0 0 6px 6px',
      minHeight: '100px',
      fontSize: '14px',
    },
    '& .ql-disabled': {
      border: 'none',
    },
    '& .ql-disabled .ql-editor': {
      padding: 0,
    },
    '& .ql-editor h1': {
      fontSize: 32,
    },
    '& .ql-editor h2': {
      fontSize: 28,
    },
    '& .ql-editor h3': {
      fontSize: 24,
    },
    '& .ql-editor h4': {
      fontSize: 20,
    },
    '& .ql-editor h5': {
      fontSize: 18,
    },
    '& .ql-editor h6': {
      fontSize: 16,
    },
  },
}));

export default function ReportDetail({ canEdit }) {
  const { id, reportId } = useParams();
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { data: reportDetails } = useReport(id, reportId);
  const [editableSectionId, setEditableSection] = useState(null); // Tracks which section is being edited
  const { mutateAsync: deleteReportSection, isPending } = useDeleteReportSection();
  const { open, show, handleConfirm, handleClose } = useConfirmationDialog();
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      // Initialize form fields with section data
      ...reportDetails?.sections?.reduce((acc, section) => {
        acc[`${section?.id}_name`] = section?.name;
        acc[`${section?.id}_description`] = section?.description;
        return acc;
      }, {}),
    },
  });
  const { mutateAsync: addToReports } = useAddToReport();

  const { mutateAsync: updateReportSection, isPending: updatePending } = useUpdateReport();
  const { mutateAsync: updateReportIndex } = useUpdateReportIndex();

  const [sections, setSections] = useState(reportDetails?.sections || []);
  // Add state for unsaved title changes
  const [unsavedTitles, setUnsavedTitles] = useState({});
  // Add state for unsaved section text changes
  const [unsavedDescriptions, setUnsavedDescriptions] = useState({});
  // store the sections map
  const [sectionsById, setSectionsById] = useState({});

  useEffect(() => {
    setSections(reportDetails?.sections || []);

    // Create the sections map for O(1) lookups
    if (reportDetails?.sections) {
      const sectionMap = reportDetails.sections.reduce((acc, section) => {
        acc[section.id] = section;
        return acc;
      }, {});
      setSectionsById(sectionMap);
    }
  }, [reportDetails]);

  useEffect(() => {
    // Update form values when reportDetails change
    reportDetails?.sections?.forEach((section) => {
      setValue(`${section?.id}_name`, section?.name);
      setValue(`${section?.id}_description`, section?.description);
    });

    // Initialize unsaved titles from reportDetails while preserving existing unsaved changes
    if (reportDetails?.sections) {
      setUnsavedTitles((prevUnsaved) => {
        const initialTitles = reportDetails.sections.reduce((acc, section) => {
          // Keep existing unsaved titles if they exist, otherwise use the original value
          acc[section.id] =
            prevUnsaved[section.id] !== undefined ? prevUnsaved[section.id] : section.name;
          return acc;
        }, {});
        return initialTitles;
      });

      // Initialize unsaved descriptions while preserving existing unsaved changes
      setUnsavedDescriptions((prevUnsaved) => {
        const initialDescriptions = reportDetails.sections.reduce((acc, section) => {
          // Keep existing unsaved descriptions if they exist, otherwise use the original value
          acc[section.id] =
            prevUnsaved[section.id] !== undefined ? prevUnsaved[section.id] : section.description;
          return acc;
        }, {});
        return initialDescriptions;
      });
    }
  }, [reportDetails, setValue]);

  // Add functions to check if content has changed
  const isTitleChanged = (sectionId) => {
    const originalTitle = sectionsById[sectionId]?.name || '';
    return unsavedTitles[sectionId] !== undefined && unsavedTitles[sectionId] !== originalTitle;
  };

  const isDescriptionChanged = (sectionId) => {
    const originalDesc = sectionsById[sectionId]?.description || '';
    return (
      unsavedDescriptions[sectionId] !== undefined &&
      unsavedDescriptions[sectionId] !== originalDesc
    );
  };

  // Handle delete action
  const handleDelete = (sid) => {
    show(() =>
      deleteReportSection(
        { studyId: id, reportId, sectionId: sid },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['report', JSON.stringify(id + reportId)] });
          },
        },
      ),
    );
  };

  // Handle title change without saving
  const handleTitleChange = (sectionId, value) => {
    setUnsavedTitles((prev) => ({
      ...prev,
      [sectionId]: value,
    }));
    setValue(`${sectionId}_name`, value);
  };

  // Handle description change without saving
  const handleDescriptionChange = (sectionId, value) => {
    const cleanedValue = value.replace(/<p><br><\/p>/g, '');
    setUnsavedDescriptions((prev) => ({
      ...prev,
      [sectionId]: cleanedValue,
    }));
    setValue(`${sectionId}_description`, cleanedValue);
  };

  const onSubmit = (data) => {
    // Use the unsaved states directly instead of form data
    const sectionData = {
      id: editableSectionId,
      // Use unsaved title if it exists, otherwise fall back to form data
      name:
        unsavedTitles[editableSectionId] !== undefined
          ? unsavedTitles[editableSectionId]
          : data[`${editableSectionId}_name`],
      // Use unsaved description if it exists, otherwise fall back to form data
      description:
        unsavedDescriptions[editableSectionId] !== undefined
          ? unsavedDescriptions[editableSectionId]
          : data[`${editableSectionId}_description`],
      index: sections.findIndex((item) => item.id === editableSectionId) + 1,
    };

    // Update unsaved states when saving
    setUnsavedTitles((prev) => ({
      ...prev,
      [editableSectionId]: sectionData.name,
    }));

    setUnsavedDescriptions((prev) => ({
      ...prev,
      [editableSectionId]: sectionData.description,
    }));

    updateReportSection(
      {
        studyId: id,
        reportId,
        sectionId: editableSectionId,
        formData: sectionData,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['report', JSON.stringify(id + reportId)] });
          setEditableSection(null); // Reset editable section after saving
        },
      },
    );
  };

  const handleAddBlock = () => {
    addToReports(
      {
        studyId: id,
        reportId,
        formData: {
          name: 'Untitled Section',
          description: 'untitled description',
          index: min(reportDetails.sections.length + 1, 100),
        },
      },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({ queryKey: ['report', JSON.stringify(id + reportId)] });

          // If we have the new section ID from the response, add it to unsaved states
          if (data && data.id) {
            setUnsavedTitles((prev) => ({
              ...prev,
              [data.id]: 'Untitled Section',
            }));

            setUnsavedDescriptions((prev) => ({
              ...prev,
              [data.id]: 'untitled description',
            }));
          }
        },
      },
    );
  };

  // Function to validate and fix image elements
  const validateAndFixImages = (htmlContent) => {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Find all images and validate them
    const images = tempDiv.querySelectorAll('img');
    images.forEach((img) => {
      // Check if image has valid dimensions
      if (!img.width || !img.height || isNaN(img.width) || isNaN(img.height)) {
        // Set default dimensions for invalid images
        img.style.width = '400px';
        img.style.height = 'auto';
        img.width = 400;
      }

      // Ensure images have proper attributes
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.margin = 'auto';
      img.style.display = 'block';
    });

    return tempDiv.innerHTML;
  };

  // Function to generate PDF with formatted HTML
  const download = async () => {
    try {
      const htmlContent = reportDetails.sections
        .map(
          (section) =>
            `<h4 style="font-size: 20px; font-weight:800">${section.name}</h4><div style="font-size: 14px;line-height: 1.5;letter-spacing: 0.5px">${section.description}</div><br>`,
        )
        .join('');

      // Validate and fix images first
      const validatedHtmlContent = validateAndFixImages(htmlContent);

      // Ensure images have a fixed width with additional safety checks
      const fixedHtmlContent = validatedHtmlContent.replace(
        /<img /g,
        '<img style="max-width: 100%;height: auto;margin:auto;display:block" width="400" ',
      );

      // Convert HTML to pdfmake format with error handling
      let pdfContent;
      try {
        pdfContent = htmlToPdfmake(
          `<h3 style="font-size: 24px; font-weight:800;margin-bottom: 20px">${reportDetails?.title}</h3>${fixedHtmlContent}`,
        );
      } catch (conversionError) {
        console.error('HTML to PDF conversion error:', conversionError);
        // Fallback: create PDF without problematic content
        pdfContent = htmlToPdfmake(
          `<h3 style="font-size: 24px; font-weight:800;margin-bottom: 20px">${reportDetails?.title}</h3><p>Error processing content. Please try again.</p>`,
        );
      }

      // Modify images within pdfContent to explicitly set width in pdfmake format
      const processElement = (element) => {
        if (element.image) {
          try {
            // Validate image data
            if (element.image && typeof element.image === 'string') {
              // Set safe default dimensions
              element.width = 400;
              element.fit = [400, 300];
              element.margin = [0, 10, 0, 10];
              element.alignment = 'center';
            } else {
              // Remove invalid image elements
              return null;
            }
          } catch (imageError) {
            console.error('Image processing error:', imageError);
            // Replace with text placeholder
            return { text: '[Image could not be processed]', margin: [0, 10, 0, 10] };
          }
        }

        // Process nested elements recursively
        if (element.stack) {
          element.stack = element.stack.map(processElement).filter(Boolean);
        }
        if (element.columns) {
          element.columns = element.columns.map(processElement).filter(Boolean);
        }

        return element;
      };

      // Process all elements safely
      const safePdfContent = pdfContent.map(processElement).filter(Boolean);

      const docDefinition = {
        content: safePdfContent,
        defaultStyle: {
          fontSize: 12,
          lineHeight: 1.2,
        },
      };

      pdfMake.createPdf(docDefinition).download('sections.pdf');
    } catch (error) {
      console.error('PDF generation error:', error);
      // Show user-friendly error message
      alert('Error generating PDF. Please try again or contact support if the problem persists.');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = sections.findIndex((item) => item.id === active.id);
      const newIndex = sections.findIndex((item) => item.id === over.id);

      const newSections = arrayMove(sections, oldIndex, newIndex);
      setSections(newSections);
      updateReportIndex(
        {
          studyId: id,
          reportId,
          formData: {
            sections: newSections.map((section, i) => ({
              id: section.id,
              index: i + 1,
            })),
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['report', JSON.stringify(id + reportId)]);
          },
        },
      );
    }
  };

  return (
    <>
      <Box sx={{ width: '772px', mx: 'auto', mt: 3 }}>
        <Box
          sx={{
            pb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
            <Typography variant="subtitle2">Reports</Typography>
            <Typography variant="subtitle2">{reportDetails?.title} </Typography>
          </Breadcrumbs>
          {/* Only show action buttons for users with edit access */}
          {canEdit && (
            <Box display={'flex'} gap={1}>
              <Button variant="custom" color="primary" onClick={handleAddBlock}>
                Add Block
              </Button>
              <Button variant="custom" color="primary" onClick={download}>
                Download
              </Button>
            </Box>
          )}
          {!canEdit && (
            <Tooltip title="You don't have permission to download this report" arrow>
              <span>
                <Button variant="custom" color="primary" onClick={download} disabled={!canEdit}>
                  Download
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
        <Grid container>
          <Grid item xs={12}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={sections} strategy={verticalListSortingStrategy}>
                {sections.map((section) => {
                  return (
                    <SortableItem key={section?.id} id={section?.id}>
                      <form onSubmit={handleSubmit(onSubmit)} key={section?.id}>
                        <Card
                          id="credit-balance"
                          variant="outlined"
                          key={section?.id}
                          sx={{
                            mb: 4,
                            p: 0,
                            opacity: !canEdit && section.id === editableSectionId ? 0.7 : 1,
                            pointerEvents:
                              !canEdit && section.id === editableSectionId ? 'none' : 'auto',
                          }}
                        >
                          <CardHeader
                            title={
                              <Box
                                display={'flex'}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                              >
                                {/* Conditionally render the section name or an input field */}
                                {editableSectionId === section?.id ? (
                                  <Box
                                    sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                  >
                                    <Controller
                                      name={`${section?.id}_name`}
                                      control={control}
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          size="small"
                                          fullWidth
                                          value={unsavedTitles[section.id] || field.value}
                                          onChange={(e) =>
                                            handleTitleChange(section.id, e.target.value)
                                          }
                                        />
                                      )}
                                    />
                                    {isTitleChanged(section.id) && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          ml: 1,
                                          color: 'info.main',
                                          fontStyle: 'italic',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        Unsaved
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="subtitle2">
                                    {unsavedTitles[section.id] || section?.name}
                                    {isTitleChanged(section.id) && (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{
                                          ml: 1,
                                          color: 'info.main',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        (Unsaved)
                                      </Typography>
                                    )}
                                  </Typography>
                                )}
                                {canEdit && (
                                  <Box>
                                    <IconButton
                                      size="small"
                                      onClick={() => setEditableSection(section?.id)}
                                    >
                                      <EditOutlined />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDelete(section?.id)}
                                    >
                                      <DeleteOutlineOutlined />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                          <Divider />
                          <CardContent className={classes.quill}>
                            <Controller
                              name={`${section?.id}_description`}
                              control={control}
                              render={({ field }) => {
                                // Determine the actual value to display - use unsaved value if it exists
                                const actualValue =
                                  unsavedDescriptions[section.id] !== undefined
                                    ? unsavedDescriptions[section.id]
                                    : field.value || '';

                                return (
                                  <>
                                    <Box sx={{ position: 'relative' }}>
                                      <ReactQuill
                                        value={actualValue}
                                        defaultValue={actualValue}
                                        onChange={(value) =>
                                          handleDescriptionChange(section.id, value)
                                        }
                                        key={
                                          editableSectionId === section?.id
                                            ? `edit-mode-${section.id}`
                                            : `view-mode-${section.id}`
                                        } // Force remount on toggle with unique key
                                        theme="snow"
                                        readOnly={editableSectionId !== section?.id} // Toggle read-only mode
                                        modules={{
                                          toolbar:
                                            editableSectionId === section?.id
                                              ? [
                                                  [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                                  ['bold', 'italic', 'underline'],
                                                  [{ list: 'ordered' }, { list: 'bullet' }],
                                                  [{ script: 'sub' }, { script: 'super' }],
                                                  [{ indent: '-1' }, { indent: '+1' }],
                                                  [{ align: [] }],
                                                  ['link', 'image'],
                                                  ['clean'],
                                                ]
                                              : false,
                                          imageResize: {
                                            parchment: Quill.import('parchment'),
                                            modules: ['Resize', 'DisplaySize'],
                                          },
                                        }}
                                      />
                                      {editableSectionId === section?.id &&
                                        isDescriptionChanged(section.id) && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              position: 'absolute',
                                              top: 5,
                                              right: 50,
                                              color: 'info.main',
                                              fontStyle: 'italic',
                                              bgcolor: 'rgba(255,255,255,0.8)',
                                              px: 1,
                                              borderRadius: 1,
                                              zIndex: 2,
                                            }}
                                          >
                                            Unsaved
                                          </Typography>
                                        )}
                                    </Box>
                                  </>
                                );
                              }}
                            />
                            {editableSectionId === section?.id && (
                              <Box mt={2} display={'flex'} justifyContent={'flex-end'} gap={1}>
                                <Button
                                  variant="custom"
                                  color="error"
                                  onClick={() => setEditableSection(null)}
                                >
                                  {' '}
                                  Cancel
                                </Button>
                                <LoadingButton
                                  loading={updatePending}
                                  type="submit"
                                  variant="contained"
                                  color="primary"
                                >
                                  Save
                                </LoadingButton>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </form>
                    </SortableItem>
                  );
                })}
              </SortableContext>
            </DndContext>
          </Grid>
        </Grid>
      </Box>
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        disabled={isPending}
        okText={isPending ? 'Deleting...' : 'Delete'}
        title="Delete Content"
        content="This action can’t be undone. Are you sure you want to delete Thematic Findings from this report?"
      />
    </>
  );
}
